import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useDialogStore } from "../state/dialogStore";
import { Kbd } from "./Kbd";

/**
 * The one dialog shell shared by the first-visit intro (GRA-137) and the
 * always-reachable data/simulation note it also backs: a centered panel over a
 * backdrop, closable by Escape, a backdrop click, or its own close control.
 * Deliberately a real interruption (unlike RevealBanner's inline treatment,
 * spec §7): the intro is seen once and dismissed for good, and the info note
 * is opened on purpose, so neither needs to preserve in-round flow the way a
 * reveal does.
 *
 * Truly modal (GRA-146, a launch-review blocker: the shared shell used to be a
 * plain `div role="dialog"` that never trapped focus, so an ArrowUp/Enter/
 * Enter typed with its close button focused reached the Trade panel behind
 * it and placed a trade). Built on the native `<dialog>` element: `showModal`
 * is called where the DOM supports it (real browsers), so the dialog is
 * promoted to the top layer and gets the platform's own modal semantics and
 * `::backdrop` for free. The Tab/Shift+Tab wrap and the Escape close are still
 * hand-rolled rather than left to the platform, for two reasons: the unit-test
 * DOM (jsdom) has no `showModal`/focus-trap implementation at all, and the
 * app also needs a single "a dialog is open" signal (`dialogStore`) that every
 * *other* keyboard shortcut in the game can consult, and the platform's own
 * trap has no such hook. `inert` goes on the app's root element, outside this
 * portal, so the rest of the document is unreachable by pointer, keyboard, or
 * the accessibility tree while this is open.
 */

export type InfoDialogProps = {
  title: ReactNode;
  /**
   * How wide the panel is allowed to get. "md" is the note-sized default;
   * "wide" is for a panel whose text needs a little more room without going
   * chart-sized (the feedback dialog); "lg" is for a panel that has to hold
   * a chart (the history review, GRA-154), which is unreadable at note
   * width.
   */
  size?: "md" | "wide" | "lg";
  /** Label read by screen readers and shown on the close control. */
  closeLabel?: string;
  onClose: () => void;
  children?: ReactNode;
};

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

function focusableIn(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

const SIZE_CLASS: Record<"md" | "wide" | "lg", string> = {
  md: "max-w-md",
  wide: "max-w-lg",
  lg: "max-w-3xl",
};

export function InfoDialog({
  title,
  size = "md",
  closeLabel = "close",
  onClose,
  children,
}: InfoDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const push = useDialogStore((s) => s.push);
  const pop = useDialogStore((s) => s.pop);

  // Runs before paint (unlike useEffect) so the dialog is never briefly
  // visible without being modal: the trigger's focus is remembered so
  // closing can restore it, the app root is made inert, `dialogStore` is
  // incremented so every other shortcut in the app goes quiet, and the
  // native modal is opened where the DOM supports it.
  useLayoutEffect(() => {
    triggerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    push();
    const root = document.getElementById("root");
    root?.setAttribute("inert", "");

    const dialog = dialogRef.current;
    if (dialog && typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog?.setAttribute("open", "");
    }
    closeRef.current?.focus();

    return () => {
      pop();
      root?.removeAttribute("inert");
      // Close the native modal before handing focus back: StrictMode runs
      // this cleanup once synchronously right after the effect above, on the
      // very same `<dialog>` node, without an intervening close. Skipping
      // `close()` here left the browser's own modal focus trap active, which
      // silently refused the restore below, so the *second* (kept) mount
      // went on to capture the still-focused close button as `triggerRef`
      // instead of the real trigger, focus.
      if (dialog?.open && typeof dialog.close === "function") dialog.close();
      triggerRef.current?.focus();
    };
    // Trigger, root and dialog are all captured once, on the one mount this
    // component ever gets (its parent unmounts it to close, rather than
    // toggling a prop); an empty dependency list is deliberate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The one path every close route (Escape, the close button, a backdrop
  // click) goes through. `dialog.close()` is called explicitly, and before
  // the focus restore: a real `showModal`'d dialog keeps the browser's own
  // focus trap active until it is closed, so focusing the trigger while it
  // is still the top-layer modal is silently refused, no matter that `#root`
  // is no longer inert by the time React gets around to unmounting it. Focus
  // is handed back *before* `onClose` unmounts this component too, rather
  // than left to the layout effect's cleanup: the close button click can
  // itself be the browser's own default action for an Enter keypress (any
  // focused `<button>` activates on Enter), and by the time React has
  // committed the unmount, that same native keydown has already moved on.
  // Restoring focus first means a trailing Enter lands on the trigger, not
  // on whatever the game left behind it.
  const requestClose = () => {
    const dialog = dialogRef.current;
    if (dialog?.open && typeof dialog.close === "function") dialog.close();
    triggerRef.current?.focus();
    onClose();
  };

  // Escape closes from anywhere in the dialog. Bound on `window`, matching
  // the rest of the app's shortcuts, rather than left to the platform's own
  // Escape-cancels-a-modal-dialog behavior: that native path is unavailable
  // in the unit-test DOM (jsdom implements neither `showModal` nor the
  // `cancel` event it would fire), and this way there is exactly one code
  // path for Escape regardless of how the dialog got shown.
  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
    // requestClose closes over the latest onClose via a stable pattern below
    // (it is recreated each render, which is fine: only the current one is
    // ever registered).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDialogElement>) => {
    if (event.key !== "Tab") return;
    const panel = dialogRef.current;
    if (!panel) return;
    const focusable = focusableIn(panel);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    // Wrap rather than escape: Tab from the last focusable goes to the
    // first, Shift+Tab from the first (or from outside the panel entirely,
    // which should not happen once inert is applied but is handled anyway)
    // goes to the last.
    if (event.shiftKey) {
      if (active === first || !panel.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !panel.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  };

  // Backdrop click closes, a click on the panel itself does not: the panel's
  // own onClick stops propagation before it reaches this one.
  const onBackdropClick = (event: ReactMouseEvent<HTMLDialogElement>) => {
    if (event.target === event.currentTarget) requestClose();
  };

  return createPortal(
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      onClick={onBackdropClick}
      onKeyDown={onKeyDown}
      className={`m-auto w-[calc(100%-2rem)] ${SIZE_CLASS[size]} border-0 bg-transparent p-0 font-mono text-sm text-fg backdrop:bg-bg/80`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded border border-muted/30 bg-surface p-5"
      >
        <div className="mb-3 flex items-start justify-between gap-4">
          <h2 id={titleId} className="text-fg">
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={requestClose}
            aria-label={closeLabel}
            className="flex min-h-8 shrink-0 items-center rounded border border-muted/40 px-3 py-1 text-muted transition-colors hover:border-accent hover:text-accent sm:min-h-6"
          >
            {closeLabel} <Kbd>Esc</Kbd>
          </button>
        </div>
        <div className="flex flex-col gap-3 text-muted [&_a]:text-accent [&_a:hover]:underline [&_strong]:font-normal [&_strong]:text-fg">
          {children}
        </div>
      </div>
    </dialog>,
    document.body,
  );
}
